import type { DiagnosticEntry, TelemetryEntry } from "@/lib/diagnostics";

const MAX_TEXT_LENGTH = 4_000;
const AUTHORIZATION_VALUE =
  /["']?(authorization)["']?\s*[:=]\s*["']?(?:bearer\s+)?([^"'\s,;}]+)/gi;
const SENSITIVE_VALUE =
  /["']?(cookie|password|passwd|secret|token|api[-_]?key)["']?\s*[:=]\s*["']?([^"'\s,;}]+)/gi;
const BEARER_VALUE = /bearer\s+[a-z0-9._~+/=-]+/gi;
const EMAIL_VALUE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
// Page routes and their API calls carry a single-use secret as the segment
// after these names: /reset-password/<t>, /auth/validate-reset-token/<t>,
// /auth/verify-email/<t>, /invitations/<t>, /estates/invitations/<t>/accept.
const TOKEN_PATH_SEGMENT =
  /\/(reset-password|verify-email|validate-reset-token|invitations)\/[^/?#\s"'\\]+/g;

export function sanitizeDiagnosticText(value: unknown): string {
  let text: string;
  if (value instanceof Error) text = `${value.name}: ${value.message}`;
  else if (typeof value === "string") text = value;
  else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }
  return text
    .replace(AUTHORIZATION_VALUE, "$1=[REDACTED]")
    .replace(SENSITIVE_VALUE, "$1=[REDACTED]")
    .replace(BEARER_VALUE, "Bearer [REDACTED]")
    .replace(EMAIL_VALUE, "[REDACTED]")
    .replace(TOKEN_PATH_SEGMENT, "/$1/[REDACTED]")
    .slice(0, MAX_TEXT_LENGTH);
}

export function sanitizeDiagnosticPath(path: string): string {
  const [pathname, query] = path.split("?", 2);
  const safePath = pathname.replace(TOKEN_PATH_SEGMENT, "/$1/[REDACTED]");
  if (!query) return safePath;
  const keys = new URLSearchParams(query);
  return `${safePath}?${[...keys.keys()]
    .map((key) => `${encodeURIComponent(key)}=[REDACTED]`)
    .join("&")}`;
}

// Every text field is redacted; page and path are URLs, so their query values
// are dropped too. Entries from storage or another tab may predate redaction,
// so the merge path runs through here as well as fresh captures.
export function scrubEntry<T extends DiagnosticEntry | TelemetryEntry>(
  entry: T,
): T {
  const clean: Record<string, unknown> = { ...entry };
  for (const key in clean) {
    const value = clean[key];
    if (typeof value === "string")
      clean[key] =
        key === "page" || key === "path"
          ? sanitizeDiagnosticPath(value)
          : sanitizeDiagnosticText(value);
  }
  return clean as T;
}
